import React, { useContext, useState, useRef } from 'react'
import Octicon, {
  Location,
  Person,
  Package,
  Lock,
  Law,
  ArrowLeft,
  Check,
  X,
  Dependent,
  Calendar,
  CreditCard,
  ChevronDown,
  Rocket,
  Comment
} from '@primer/octicons-react'
import { useTranslation } from 'react-i18next'
import Loader from 'react-loader-spinner'
import Cookie from 'js-cookie'
import { useToasts } from 'react-toast-notifications'
import { useMutation, useQueryClient } from 'react-query'
import { ConfirmButton, Button } from '@viadelivery/design-system'
import styles from '~/styles/orderDetails.module.css'

import {
  SessionContext,
  ConfirmModal,
  OrderDetailsHistory,
  OrderDateFormatter,
} from '../../components'
import { QUERY_TYPES, STATUS_LIST } from '~/constants'
import { ORDER_DETAILS } from '~/types'
import { Container } from './styles'
import { postData } from '~/utils/api'
import {
  dateFormatter,
  currencyFormatter,
  lengthByLocaleFormatter,
  weightByLocaleFormatter,
} from '../../utils/formatters'
import { useAtom } from 'jotai'
import { catchFocusAtom, modalDeliveryAddressAtom } from '../../states'
import OrderHistory from '../OrderHistory'
import useToggle from '~/utils/hooks/useToggle'
import OrderListLongPolling from '~/utils/OrderListLongPolling'
import Measurements from '~/utils/Measurements'
import PricingDetails from './PricingDetails'
import Multipackage from './Multipackage'

// For PPN interface
import {
  AcceptOrderButton,
  IssueOrderButton,
  ReturnOrderButton,
  ActionsButtonsGroup
} from '~/components/PPN/ordersActions'

import GetLabelButton from '../OrdersPage/GetLabelButton'

let reasonST = null
const enCountries = ['US', 'GB', 'UK', 'CA']

const {
  APPROVED,
  CANCEL_REQUEST,
  CANCELED,
  CANCELLED,
  CREATED,
  EXPIRING,
  FIRST_MILE,
  HOLD,
  PICKED_UP,
  PLACED_IN_POSTAMAT,
  PROBLEM,
  REJECTED,
  UNCLAIMED,
  IN_PROGRESS,
  RETURNED,
  RETURNING,
  FULFILLMENT
} = STATUS_LIST
const { ORDERS, ORDER_INFO } = QUERY_TYPES

interface Props {
  order: ORDER_DETAILS
  loading?: boolean
  ppnInterface?: boolean
  locale?: string
}

const OrderDetails = ({
  order,
  loading = false,
  ppnInterface = false,
  locale = 'en',
  hint = {},
  timezone = 0,
  isSyntheticDealer,
  onOrderCancel,
  onClose,
  isOrders,
  ordersWithShownItems,
  setOrderListUpdater,
  clickedOrder,
  authDealerId,
  isHoursAndMinutesShown,
  onAction,
  stage,
}: Props) => {
  let {
    ext_key,
    history,
    id,
    in_cust_email,
    in_cust_name,
    in_cust_phone,
    in_cust_pin,
    in_height,
    in_length,
    in_weight,
    in_width,
    in_shop,
    in_shop_id,
    paid,
    service_name, 
    status,
    uuid,
    point_address,
    point_description,
    delivery_retail_number,
    delivery_barcode,
    delivery_tracking,
    tracking_url,
    partner,
    paid_at,
    in_point_uuid,
    delivery_date,
    expiration_date,
    city,
    in_cost,
    in_delivery_cost,
    in_total,
    in_delivery_cost_dealer,
    is_delivery_complete,
    currency,
    packageName,
    packagePrice,
    authorName,
    originName,
    stickerUrl,
  } = order
	const regExp = /^\+([\d]{1,})([\d]{3})([\d]{3})([\d]{2})([\d]{2})$/
	if (typeof +(in_cust_phone) === 'number' && (regExp).test(in_cust_phone + '')) {
		in_cust_phone = (in_cust_phone + '').replace(regExp, '+$1 $2 $3-$4$5')
	}

  const orderListLongPolling = new OrderListLongPolling(setOrderListUpdater)
  let canBeCancelled = [
    CREATED,
    APPROVED,
    FIRST_MILE,
    REJECTED,
    HOLD,
  ].includes(status)
  if (order.dealer_country === 'RU' && status === 'IN_PROGRESS') {
    canBeCancelled = true
  }
  const canNotify =
    [PLACED_IN_POSTAMAT, EXPIRING].includes(status) &&
    process.env.NEXT_PUBLIC_NOTIFY_API_URL?.length
  const canExtendStorage =
    [PLACED_IN_POSTAMAT, EXPIRING].includes(status) &&
    process.env.NEXT_PUBLIC_EXTEND_STORAGE_API_URL?.length
  const [, setModalOpen] = useAtom(modalDeliveryAddressAtom)
  const { t } = useTranslation(['common', 'Orders'])
  const { addToast } = useToasts()
  const [isPricingDetailsVisible, setPricingDetailsVisible] = useToggle()
  const queryClient = useQueryClient()
  const { currentDealer } = useContext(SessionContext)
  const { api_key } = currentDealer
  const [ isCancelOrderStarted, setIsCancelOrderStarted ] = useState(false)
  const [ isCancelReasonRequired, setIsCancelReasonRequired ] = useState(false)
  const [ isCancelOrderInProgress, setIsCancelOrderInProgress ] = useState(false)
  const [ cancelReason, setCancelReason ] = useState('')
  const textareaRef = useRef(null)
  const { mutate, isLoading } = useMutation(
    () => {
      const { session } = Cookie.get()
      const currentDate = Date.now()
      let options = {
        session,
        uuid: currentDealer.uuid,
        dealer_token: currentDealer.dealer_token,
        ext_key,
        reason: cancelReason,
        origin: window?.location?.origin,
        currentDate,
      }
      if (currentDealer.uuid !== in_shop_id) {
        options.shipper_id = in_shop_id
      }
      onAction.send({
          action: 'Cancel an order',
          actionData: {
            file: 'components/OrdersDetails/index.tsx',
            requestUrl: '/api/v1/cancel-order',
            requestData: options,
          },
      })
      // orderListLongPolling.updateOrderList(currentDate)
      return postData('/api/v1/cancel-order', options)
    },
    {
      onSuccess: () => {
        addToast(t('Orders:Successfully cancelled', { ext_key }), {
          appearance: 'success',
        })
        queryClient.invalidateQueries(ORDERS)
        queryClient.invalidateQueries([ORDER_INFO, { id }])
        onOrderCancel()
      },
      onError: () => {
        addToast(t('Orders:Could not cancel the order'), {
          appearance: 'error',
        })
        onOrderCancel()
      },
    },
  )

  const onCancel = (type, event) => {
    if (!isCancelOrderInProgress) {
      if (type === 'start') {
        setIsCancelOrderStarted(clickedOrder)
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.focus()
            onCancel('reasonChanged', textareaRef.current.value)
          }
        })
      } else if (type === 'cancel') {
        setIsCancelOrderStarted(false)
      } else if (type === 'reason') {
        if (reasonST) {
          clearTimeout(reasonST)
        }
        reasonST = setTimeout(() => {
          const value = event.target.value
          onCancel('reasonChanged', value)
          setCancelReason(value)
        }, 300)
      } else if (type === 'reasonChanged') {
        const value = event?.trim('')
        if (value.length > 3) {
          if (isCancelReasonRequired) {
            setIsCancelReasonRequired(false)
          }
        } else {
          if (!isCancelReasonRequired) {
            setIsCancelReasonRequired(true)
          }
        }
      } else if (type === 'do') {
        if (!isCancelReasonRequired) {
          setIsCancelOrderInProgress(true)
          mutate()
          setTimeout(() => {
            setIsCancelOrderStarted(false)
            setIsCancelOrderInProgress(false)
          }, 5000)
        }
      }
    }
  }

  let phone = order?.in_cust_phone + ''
  if (order && order.dealer_country) {
    if (enCountries.includes(order.dealer_country)) {
      if (phone.length === 10) {
        phone = 1 + phone
      }
    } else if (order.dealer_country === 'GB') {
      if (phone.length <= 10) {
        phone = 44 + phone
      }
    }
    if (!phone.startsWith('+')) phone = `+${phone}` // TODO: phone number should be formatted depending on country
  }
  const isOrder = !!(Object.keys(order).length)
  const isMultipleOrdersOpened = !!(Object.keys(ordersWithShownItems || {}).length > 1)
  if (!isOrder && !isMultipleOrdersOpened && !loading) {
    return null
  }
  const dimensionLocale = (order.dealer_dimensions_mu === 'in' ? 'en' : 'ru')
  const weightLocale = (order.dealer_weight_mu === 'lb' ? 'en' : 'ru')
  if (isOrder || loading) {
    return (
      <Container className={"text-gray-700 bg-white p-6 shadow-lg rounded mt-8 " + styles.main}>
        {loading ? (
          <div className="flex justify-center">
            <Loader type="Oval" color="#F64F2A" height={50} width={50} />
          </div>
        ) : (isOrder ? (
          <>
            <div className="mb-6">
              <h4 className={'font-semibold text-gray-800 text-xl mb-1'}>
                {t('Order')} #{ext_key}
              </h4>
              <div className={styles.ids}>
                <div>
                  {order.uuid}
                </div>
                <div>
                  {order.externalId ? order.externalId : null}
                </div>
              </div>
              <div className="mb-1 text-sm flex flex-wrap">
                {in_shop && (
                  <div className="w-full flex flex-col">
                    <div className="rounded bg-gray-200 m-1 py-1 px-2">
                      <span className="font-bold pr-1">{t('Dealer Name')}:</span>
                      <span>{in_shop}</span>
                    </div>
                    {authorName && in_shop !== authorName && (
                      <div className="rounded bg-gray-200 m-1 py-1 px-2">
                        <span className="font-bold pr-1">{t('Author Name')}:</span>
                        <span>{authorName}</span>
                      </div>
                    )}
                    {originName && in_shop !== originName && (
                      <div className="rounded bg-gray-200 m-1 py-1 px-2">
                        <span className="font-bold pr-1">{t('Origin Name')}:</span>
                        <span>{originName}</span>
                      </div>
                    )}
                  </div>
                )}
                {delivery_tracking && (
                  <div className="w-full flex">
                    <div className="rounded bg-gray-200 m-1 py-1 px-2">
                      <Multipackage
                        order={order}
                        type="tracking_numbers"
                        authDealerId={authDealerId}
                        t={t}
                      />
                    </div>
                  </div>
                )}
                {delivery_retail_number && (
                  <div className="w-full flex">
                    <div className="rounded bg-gray-200 m-1 py-1 px-2">
                      <span className="font-bold pr-1">
                        {t('Orders:Retail Number')}:
                      </span>
                      <span>{delivery_retail_number}</span>
                    </div>
                  </div>
                )}
                {delivery_barcode && (
                  <div className="w-full flex">
                    <div className="rounded bg-gray-200 m-1 py-1 px-2">
                      <span className="font-bold pr-1">
                        {t('Orders:Delivery barcode')}:
                      </span>
                      <span>{delivery_barcode}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {in_cust_pin && !ppnInterface && (
              <div className="flex mb-4">
                <div className={"flex-1 mr-1 " + styles.detailsIconFrame}>
                  <Octicon icon={Lock} />
                </div>
                <div className="flex-7">{in_cust_pin}</div>
              </div>
            )}
            {in_length && in_width && in_height && (
              <div className="flex mb-4">
                <Multipackage
                  order={order}
                  type="dimensions"
                  authDealerId={authDealerId}
                  t={t}
                />
              </div>
            )}

            <div className="flex mb-4">
              <Multipackage
                order={order}
                type="weights"
                authDealerId={authDealerId}
                t={t}
              />
            </div>

            {packageName && (
              <div className="flex mb-4">
                <div className={"flex-1 mr-1 " + styles.detailsIconFrame}>
                  <Octicon icon={Package} />
                </div>
                <div className="flex-7 break-all">
                  <span className="text-gray-400 text-sm">
                    {t('Orders:Package name') + ': '}
                  </span>
                  <span>
                    {packageName}
                  </span>
                </div>
              </div>
            )}

            <div className="flex mb-4">
              <div className={"flex-1 mr-1 " + styles.detailsIconFrame}>
                <Octicon icon={Location} />
              </div>
              <div className="flex-7 break-all">
                {point_description && (
                  <>
                    <span className="font-semibold">{point_description}</span>{' '}
                    <br />{' '}
                  </>
                )}
                {point_address && point_address}

                {partner && (
                  <div className="my-1">
                    <span className="font-bold pr-1">{t('Orders:Partner')}:</span>
                    <span>{partner}</span>
                  </div>
                )}

                {!ppnInterface && (
                  <>
                    <div className="text-gray-400 text-xs">
                      {in_point_uuid}
                    </div>
                    <div className="mt-3">
                      <div
                        className={styles.toButtons}
                      >
                        <Button
                          onClick={e => {
                            e.preventDefault()
                            setModalOpen(true)
                          }}
                          size="sm"
                          variant="info"
                        >
                          {t('Orders:View on map')}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="flex mb-4">
              <div className={"flex-1 mr-1 " + styles.detailsIconFrame}>
                <Octicon icon={Person} />
              </div>
              <div className="flex-7 break-all">
                {in_cust_name && (
                  <>
                    <span className="font-semibold">{in_cust_name}</span> <br />
                  </>
                )}
                {in_cust_email && (
                  <>
                    {in_cust_email}
                    <br />
                  </>
                )}
                {in_cust_phone && (
                  <>
                    {phone}
                    <br />
                  </>
                )}
                {city && city}
              </div>
            </div>

            <div className="flex mb-4">
              <div className={"flex-1 mr-1 " + styles.detailsIconFrame}>
                {paid ? (
                  <Octicon icon={Check} className="text-green-400" />
                ) : (
                  <Octicon icon={X} className="text-red-400" />
                )}
              </div>
              <div className="flex-7 break-all">
                <span className="mr-2">{paid ? t('Paid') : t('Unpaid')}</span>
                <span>
                  {service_name && (
                    <>
                      <span className="text-gray-400 text-sm">
                        <span>(</span>
                        <span>{t(`Orders:${service_name}`)} </span>
                        <span>{
                          paid_at && `${dateFormatter(paid_at, locale, 0, 0, isHoursAndMinutesShown)}`}
                        </span>
                        <span>)</span>
                      </span>
                      {service_name == 'Online' && (
                        <div className="mt-3">
                          <Button
                            onClick={e => {
                              e.preventDefault()
                              window.open(
                                `https://pay.viadelivery.pro/order/${uuid}`,
                                '_blank',
                              )
                            }}
                            size="sm"
                            variant="info"
                          >
                            {t('Orders:Payment link')}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </span>
              </div>
            </div>

            <PricingDetails
              order={order}
              t={t}           
            />

            {delivery_date && (
              <div className="flex mb-4">
                <div className={"flex-1 mr-1 " + styles.detailsIconFrame}>
                  <Octicon icon={Dependent} />
                </div>
                <div className="flex-7 break-all">
                  <b>{t('Orders:Delivery Date')}</b>
                  <br />
                  <OrderDateFormatter
                    date={delivery_date}
                    type="delivery_date"
                    picked_up={order.picked_up}
                    row={order}
                  >
                    {dateFormatter(delivery_date, locale, 0, 0, isHoursAndMinutesShown)}
                  </OrderDateFormatter>
                </div>
              </div>
            )}

            {expiration_date && (
              <div className="flex mb-4">
                <div className={"flex-1 mr-1 " + styles.detailsIconFrame}>
                  <Octicon icon={Calendar} />
                </div>
                <div className="flex-7 break-all">
                  <b>{t('Orders:Expiration Date')}</b>
                  <br />
                  <OrderDateFormatter
                    date={expiration_date}
                    type="expiration_date"
                    row={{ status }}
                  >
                    {dateFormatter(expiration_date, locale, 0, 0, isHoursAndMinutesShown)}
                  </OrderDateFormatter>
                </div>
              </div>
            )}

            {order.customer_comment && (
              <div className="flex mb-4">
                <div className={"flex-1 mr-1 " + styles.detailsIconFrame}>
                  <Octicon icon={Comment} />
                </div>
                <div className="flex-7">
                  {order.customer_comment}
                </div>
              </div>
            )}

            <GetLabelButton
              show={order.isGetLabelButtonShown}
              isFormatsShown={true}
              order={order}
              stage={stage}
            />

            {canBeCancelled && !ppnInterface && (
              <div
                className={styles.cancelTheOrder}
              >
                <div
                  className={isCancelOrderStarted === clickedOrder ? styles.hidden : null}
                  onClick={onCancel.bind(this, 'start')}
                >
                  {t('Cancel order')}
                </div>
                <div
                  className={isCancelOrderStarted === clickedOrder ? null : styles.hidden}
                >
                  <textarea
                    rows={2}
                    ref={textareaRef}
                    className={isCancelOrderInProgress ? styles.disabled : null}
                    onKeyDown={onCancel.bind(this, 'reason')}
                    placeholder={t('Reason')}
                  />
                  <div
                    className={isCancelReasonRequired ? null : styles.hidden}
                  >
                    {t('Required')}
                  </div>
                </div>
                <div
                 className={isCancelOrderStarted === clickedOrder ? null : styles.hidden} 
                >
                  <div
                    className={isCancelOrderInProgress ? styles.disabled : null}
                    onClick={onCancel.bind(this, 'cancel')}
                  >
                    {t('Go back')}
                  </div>
                  <div
                    className={isCancelReasonRequired || isCancelOrderInProgress ? styles.disabled : null}
                    onClick={onCancel.bind(this, 'do')}
                  >
                    {t('Cancel order')}
                  </div>
                </div>
              </div>
            )}

            {canNotify && !ppnInterface && (
              <ConfirmButton
                onConfirm={() => {}}
                loading={isLoading}
                cancelLabel={t('Cancel')}
                confirmLabel={t('Notify customer')}
                size="sm"
                variant="smoke"
              >
                {t('Notify customer')}
              </ConfirmButton>
            )}

            {canExtendStorage && !ppnInterface && (
              <ConfirmButton
                onConfirm={() => {}}
                loading={isLoading}
                cancelLabel={t('Cancel')}
                confirmLabel={t('Extend storage')}
                size="sm"
                variant="smoke"
              >
                {t('Extend storage')}
              </ConfirmButton>
            )}

            {history && <OrderHistory 
              locale={locale}
              history={history}
              order={order}
              timezone={order.timezoneShift}
              t={t} hint={hint}
              isHoursAndMinutesShown={isHoursAndMinutesShown}
            />}

            {ppnInterface && (
              <div className="mt-4">
                <ActionsButtonsGroup>
                  <AcceptOrderButton order={order} t={t} withLabel />
                  <IssueOrderButton order={order} t={t} withLabel />
                  {locale === 'ru' && (
                    <ReturnOrderButton order={order} t={t} withLabel />
                  )}
                </ActionsButtonsGroup>
              </div>
            )}
          </>
        ) : (
          <div
            className={"font-bold text-gray-500 " + styles.emptyOrderDetails}
          >
            <Octicon icon={ArrowLeft} className="mr-2" />
            {t('Orders:Select an order from the table')}
          </div>
        ))}
        <div
          className={styles.toCloseDetails}
          onClick={onClose}
        />
      </Container>
    )
  } else {
    return null
  }
}

export default OrderDetails
